/**
 * Headlight Platform Configuration — top-level header topics + KPI metadata.
 *
 * The header is a set of INDEPENDENT destinations. Each is a page with KPIs proximal to its function and
 * the category browser pre-filtered (Scope) to the relevant sections. `Settings` is the catch-all — the
 * full browser (no Scope, all KPIs, the navigation-style chooser) and the app's landing. KPI values come
 * from AppData.DashboardStats.*Count (see the app's loadDashboardStatsAsync). Anything flagged
 * `Headlight: true` is gated to the Headlight customer session (CustomerID === 1).
 */

// Each Dashboard count → display metadata. Keys match AppData.DashboardStats.*.
const KPI_META =
{
	CustomerCount:            { Label: 'Customers',         Icon: 'fas fa-city',              Accent: 'var(--theme-color-brand-primary, #156dd1)',  Headlight: true },
	UserCount:                { Label: 'Users',             Icon: 'fas fa-users',             Accent: 'var(--theme-color-status-info, #2b8aa7)' },
	RoleCount:                { Label: 'Roles',             Icon: 'fas fa-user-shield',       Accent: 'var(--theme-color-brand-primary, #156dd1)' },
	ProjectCount:             { Label: 'Projects',          Icon: 'fas fa-folder-open',       Accent: 'var(--theme-color-status-success, #3a9b5c)' },
	ProjectTypeCount:         { Label: 'Project Types',     Icon: 'fas fa-diagram-project',   Accent: 'var(--theme-color-status-info, #2b8aa7)' },
	ObservationManifestCount: { Label: 'Observation Types', Icon: 'fas fa-clipboard-check',   Accent: 'var(--theme-color-status-error, #cf5b5b)' },
	OrganizationCount:        { Label: 'Organizations',     Icon: 'fas fa-building',          Accent: 'var(--theme-color-status-warning, #d08a2c)' },
	DocumentTypeCount:        { Label: 'Document Types',    Icon: 'fas fa-file-lines',        Accent: 'var(--theme-color-status-warning, #d08a2c)' },
	ContractTypeCount:        { Label: 'Contract Types',    Icon: 'fas fa-file-contract',     Accent: 'var(--theme-color-brand-primary, #156dd1)' },
	MaterialCount:            { Label: 'Materials',         Icon: 'fas fa-cube',              Accent: 'var(--theme-color-status-info, #2b8aa7)' },
	PayItemCount:             { Label: 'Pay Items',         Icon: 'fas fa-dollar-sign',       Accent: 'var(--theme-color-status-success, #3a9b5c)' },
	SampleCount:              { Label: 'Samples',           Icon: 'fas fa-vial',              Accent: 'var(--theme-color-status-error, #cf5b5b)' },
	FundingSourceCount:       { Label: 'Funding Sources',   Icon: 'fas fa-building-columns',  Accent: 'var(--theme-color-brand-primary, #156dd1)' },
	AccountCount:             { Label: 'Accounts',          Icon: 'fas fa-money-check-dollar', Accent: 'var(--theme-color-status-warning, #d08a2c)' },
};

// The "<Entity-plural>/Count" endpoints fetched at login. A failed count degrades to '--'.
const KPI_ENDPOINTS =
[
	{ Key: 'CustomerCount', Endpoint: 'Customers/Count' },
	{ Key: 'UserCount', Endpoint: 'Users/Count' },
	{ Key: 'ProjectCount', Endpoint: 'Projects/Count' },
	{ Key: 'RoleCount', Endpoint: 'Roles/Count' },
	{ Key: 'ObservationManifestCount', Endpoint: 'ObservationManifests/Count' },
	{ Key: 'OrganizationCount', Endpoint: 'Organizations/Count' },
	{ Key: 'DocumentTypeCount', Endpoint: 'ReportNamedInstances/Count' },
	{ Key: 'ContractTypeCount', Endpoint: 'ContractTypes/Count' },
	{ Key: 'ProjectTypeCount', Endpoint: 'ProjectTypes/Count' },
	{ Key: 'MaterialCount', Endpoint: 'Materials/Count' },
	{ Key: 'PayItemCount', Endpoint: 'PayItems/Count' },
	{ Key: 'SampleCount', Endpoint: 'Samples/Count' },
	{ Key: 'FundingSourceCount', Endpoint: 'FundingSources/Count' },
	{ Key: 'AccountCount', Endpoint: 'Accounts/Count' },
];

// The headline platform counts shown on the Settings (full-browser) jumbotron.
const ALL_KPI_KEYS =
[
	'CustomerCount', 'UserCount', 'RoleCount', 'OrganizationCount',
	'ProjectCount', 'ProjectTypeCount',
	'ObservationManifestCount',
	'DocumentTypeCount', 'ContractTypeCount',
];

// Header topics. Scope = navigation-graph category hashes (the nav is pre-filtered to these). The
// 'Settings' topic is the catch-all (no Scope → everything) + the navigation-style chooser + the landing.
const TOPIC_CONFIGS =
{
	Settings:      { Key: 'Settings', Title: 'Settings', Icon: 'fas fa-gear', Blurb: 'Everything you can configure — search, or pick an area below.', Route: '#/Settings', Scope: null, KpiKeys: ALL_KPI_KEYS, ShowChooser: true },
	Users:         { Key: 'Users', Title: 'Users & Access', Icon: 'fas fa-user-shield', Blurb: 'Who can sign in and what they can do.', Route: '#/Users', Scope: [ 'people-access' ], KpiKeys: [ 'UserCount', 'RoleCount' ] },
	Projects:      { Key: 'Projects', Title: 'Projects & Contracts', Icon: 'fas fa-folder-open', Blurb: 'Units of work, their templates, and the contracts that group them.', Route: '#/Projects', Scope: [ 'projects' ], KpiKeys: [ 'ProjectCount', 'ProjectTypeCount', 'ContractTypeCount' ] },
	Observations:  { Key: 'Observations', Title: 'Observations', Icon: 'fas fa-clipboard-check', Blurb: 'What gets collected in the field, and how it is approved.', Route: '#/Observations', Scope: [ 'observations' ], KpiKeys: [ 'ObservationManifestCount' ] },
	Documents:     { Key: 'Documents', Title: 'Forms & Documents', Icon: 'fas fa-file-lines', Blurb: 'Report and form templates, and how they are reviewed.', Route: '#/Documents', Scope: [ 'documents' ], KpiKeys: [ 'DocumentTypeCount' ] },
	Database:      { Key: 'Database', Title: 'Database', Icon: 'fas fa-database', Blurb: 'Explore the operational data graph, plus the reference, materials, and financial data the platform draws from.', Route: '#/Database', Scope: [ 'data-explorer', 'configuration', 'master-lists', 'materials-program', 'contracts-payments', 'physical-assets' ], KpiKeys: [ 'MaterialCount', 'PayItemCount', 'SampleCount', 'FundingSourceCount', 'AccountCount' ] },
	Lake:          { Key: 'Lake', Title: 'Data Lake', Icon: 'fas fa-water', Blurb: 'Browse imported clones and computed projections, and create new projections from your Headlight data.', Route: '#/Datalake', Scope: [ 'asset-management' ], KpiKeys: [] },
};

// The top navbar order. (The navbar markup lives in html/index.html; this is the canonical order + targets.)
const TOP_NAV =
[
	{ Label: 'Settings', Icon: 'fas fa-gear', Route: '#/Settings' },
	{ Label: 'Users', Icon: 'fas fa-user-shield', Route: '#/Users' },
	{ Label: 'Projects', Icon: 'fas fa-folder-open', Route: '#/Projects' },
	{ Label: 'Observations', Icon: 'fas fa-clipboard-check', Route: '#/Observations' },
	{ Label: 'Documents', Icon: 'fas fa-file-lines', Route: '#/Documents' },
	{ Label: 'Database', Icon: 'fas fa-database', Route: '#/Database' },
	{ Label: 'Data Lake', Icon: 'fas fa-water', Route: '#/Datalake' },
];

/** Format a raw count for display (numbers get thousands separators; placeholders pass through). */
function formatCount(pValue)
{
	if (pValue == null) { return '—'; }
	if (pValue === '...') { return '…'; }
	const tmpNumber = Number(pValue);
	return Number.isNaN(tmpNumber) ? String(pValue) : tmpNumber.toLocaleString('en-US');
}

/**
 * Build KPI display records (data, not HTML) for a set of stat keys, dropping Headlight-gated KPIs for
 * non-Headlight sessions. Feeds the {~TS:Nav-Kpi-Card:~} strip.
 * @param {Object} pStats - AppData.DashboardStats
 * @param {Array<string>} pKeys
 * @param {boolean} pIsHeadlight
 * @returns {Array<{Key:string, Label:string, Icon:string, Accent:string, Value:string}>}
 */
function buildKpiRecords(pStats, pKeys, pIsHeadlight)
{
	const tmpStats = pStats || {};
	return (pKeys || [])
		.filter((pKey) => KPI_META[pKey] && (!KPI_META[pKey].Headlight || pIsHeadlight))
		.map((pKey) =>
		{
			const tmpMeta = KPI_META[pKey];
			return { Key: pKey, Label: tmpMeta.Label, Icon: tmpMeta.Icon, Accent: tmpMeta.Accent, Value: formatCount(tmpStats[pKey]) };
		});
}

// Shared KPI-card template (one small wide rectangle; no navigation).
const KPI_CARD_TEMPLATE =
{
	Hash: 'Nav-Kpi-Card',
	Template: /*html*/`<div class="nav-kpi-rect"><span class="nav-kpi-ic" style="color:{~D:Record.Accent~}"><i class="{~D:Record.Icon~}"></i></span><span class="nav-kpi-text"><span class="nav-kpi-val">{~D:Record.Value~}</span><span class="nav-kpi-lbl">{~D:Record.Label~}</span></span></div>`,
};

module.exports = { KPI_META, KPI_ENDPOINTS, ALL_KPI_KEYS, TOPIC_CONFIGS, TOP_NAV, formatCount, buildKpiRecords, KPI_CARD_TEMPLATE };
